// bm-design-system: table primitive
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * A real `<table>` for dense, multi-column data — run history, delivery lists,
 * query results. For key/value detail rows use `DataTable` (a `<dl>`) instead.
 *
 * `Table` renders the scroll container itself: wide content scrolls inside its
 * own box so the page body never scrolls sideways. Shells constrain content to
 * `max-w-4xl`, widening to `max-w-7xl` on `2xl` screens — so a table with more
 * than four or five columns will still need it on a laptop.
 */
const Table = React.forwardRef<
  HTMLTableElement,
  React.TableHTMLAttributes<HTMLTableElement> & { containerClassName?: string }
>(({ className, containerClassName, ...props }, ref) => (
  <div className={cn("table-scroller", containerClassName)}>
    <table ref={ref} className={cn("table", className)} {...props} />
  </div>
));
Table.displayName = "Table";

const TableHead = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={className} {...props} />
));
TableHead.displayName = "TableHead";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={className} {...props} />
));
TableBody.displayName = "TableBody";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr ref={ref} className={className} {...props} />
));
TableRow.displayName = "TableRow";

export interface TableCellProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** Right-align and tabular-figure a number column. */
  numeric?: boolean;
  /** Keep the cell on one line. */
  nowrap?: boolean;
  /**
   * Pin the cell against the left edge while the rest of the table scrolls
   * under it. Apply it to the same column in every row, header included, and
   * only on tables wide enough to actually scroll.
   */
  sticky?: boolean;
}

const TableHeaderCell = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    numeric?: boolean;
    sticky?: boolean;
  }
>(({ className, numeric, sticky, scope = "col", ...props }, ref) => (
  <th
    ref={ref}
    scope={scope}
    className={cn(numeric && "cell-numeric", sticky && "cell-sticky", className)}
    {...props}
  />
));
TableHeaderCell.displayName = "TableHeaderCell";

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, numeric, nowrap, sticky, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        numeric && "cell-numeric",
        nowrap && "cell-nowrap",
        sticky && "cell-sticky",
        className,
      )}
      {...props}
    />
  ),
);
TableCell.displayName = "TableCell";

/** A full-width "nothing here" row spanning every column. */
const TableEmpty = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & { colSpan: number }
>(({ className, children, ...props }, ref) => (
  <tr>
    <td
      ref={ref}
      className={cn("px-3 py-6 text-center text-ink-muted", className)}
      {...props}
    >
      {children}
    </td>
  </tr>
));
TableEmpty.displayName = "TableEmpty";

export {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  TableEmpty,
};
